import { useEffect } from 'react';

import { Loader2 } from 'lucide-react';
import { Controller } from 'react-hook-form';

import type { ClientId } from '@nesso/ledger-contracts/ids';
import { Button } from '@nesso/ui/primitives/button';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';
import { SheetBody, SheetFooter } from '@nesso/ui/primitives/sheet';

import type { Payment } from '@/entities/payment';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';

import { useRefundPaymentForm } from '../../../model/use-refund-payment-form';
import { RefundPaymentSheetDate } from './refund-payment-sheet-form/refund-payment-sheet-date';
import { RefundPaymentSheetMethod } from './refund-payment-sheet-form/refund-payment-sheet-method';
import { RefundPaymentSheetServices } from './refund-payment-sheet-form/refund-payment-sheet-services';

type Props = {
    clientId: ClientId;
    payment: Payment;
    onClose: () => void;
    onPendingChange: (pending: boolean) => void;
};

export function RefundPaymentSheetForm({ clientId, payment, onClose, onPendingChange }: Props) {
    const refundPayment = useRefundPaymentForm({ clientId, payment, onSuccess: onClose });
    const { control, formState } = refundPayment.form;
    const controlsDisabled = refundPayment.isPending || refundPayment.isLocked;

    useEffect(() => {
        onPendingChange(refundPayment.isPending);
        return () => onPendingChange(false);
    }, [refundPayment.isPending, onPendingChange]);

    return (
        <form onSubmit={refundPayment.onSubmit} className="flex min-h-0 flex-1 flex-col">
            <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto pt-0">
                <RefundPaymentSheetServices appliedTo={payment.appliedTo} />
                <RefundPaymentSheetMethod
                    value={refundPayment.method}
                    error={formState.errors.method?.message}
                    disabled={controlsDisabled}
                    onValueChange={refundPayment.selectMethod}
                >
                    <div className="flex items-start gap-3">
                        <RefundPaymentSheetDate
                            value={refundPayment.refundDate}
                            payment={payment}
                            disabled={controlsDisabled}
                            onChange={refundPayment.selectRefundDate}
                        />
                        {refundPayment.method === 'CHECK' && (
                            <Controller
                                control={control}
                                name="checkNumber"
                                render={({ field, fieldState }) => (
                                    <InputField
                                        name={field.name}
                                        ref={field.ref}
                                        id="refund-payment-check-number"
                                        label="Check No"
                                        fieldClassName="w-[127px]"
                                        value={field.value ?? ''}
                                        onChange={(event) =>
                                            field.onChange(event.target.value || undefined)
                                        }
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message ?? fieldState.invalid}
                                        disabled={controlsDisabled}
                                    />
                                )}
                            />
                        )}
                    </div>
                </RefundPaymentSheetMethod>
                {formState.errors.root && <FieldError>{formState.errors.root.message}</FieldError>}
            </SheetBody>
            <SheetFooter>
                <Button
                    type="button"
                    variant="ghost"
                    disabled={refundPayment.isPending}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button type="submit" disabled={refundPayment.isPending}>
                    {refundPayment.isPending && <Loader2 className="animate-spin" />}
                    {`Refund ${formatCurrencyFromCents(payment.amountCents)}`}
                </Button>
            </SheetFooter>
        </form>
    );
}
